import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import CTA from '../components/CTA';
import OurCommitment from '../components/OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const CompanyPage = () => {
  const location = useLocation();
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Debug log to verify component is loading
  console.log('CompanyPage component loaded');

  // Handle hash navigation for smooth scrolling
  useEffect(() => {
    console.log('CompanyPage useEffect triggered');
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        // Add a small delay to ensure the page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100);
      }
    } else {
      // If no hash, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.hash]);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to handle smooth scrolling to sections
  // const scrollToSection = (sectionId) => {
  //   const element = document.getElementById(sectionId);
  //   if (element) {
  //     element.scrollIntoView({ 
  //       behavior: 'smooth', 
  //       block: 'start' 
  //     });
  //   }
  // };
  // const techStack = [
  //   'Generative AI', 'Agentic AI', 'LangChain', 'LlamaIndex', 'RAG', 'FAISS', 'Pinecone',
  //   'PyTorch', 'TensorFlow', 'FastAPI', 'Python', 'PostgreSQL', 
  //   'Docker', 'Kubernetes', 'AWS', 'GCP'
  // ];
  return (
    <>




      {/* Hero Section */}
      <section className="company-hero-section">
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              
              {/* Main Title */}
              <h1 className="company-hero-title">
                Turn your data into decisions with{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>production-ready AI</span>
              </h1>
              
              {/* Subtitle */}
              <p className="company-hero-subtitle">
                Transform your business with cutting-edge AI solutions and accelerate your digital transformation. We deliver intelligent systems through Generative AI, Machine Learning, NLP, and Cybersecurity AI, backed by proven expertise in custom software development and staff augmentation, driving real business outcomes.
              </p>
              
              {/* Stats Grid */}
              <div className="company-stats-grid">
                <div className="row g-4 justify-content-center">
                  <div className="col-6 col-md-3">
                    <div className="company-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="company-stat-icon">
                        <i className="bi bi-rocket-takeoff"></i>
                      </div>
                      <div className="company-stat-number">10+</div>
                      <div className="company-stat-label">Projects Delivered</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="company-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="company-stat-icon">
                        <i className="bi bi-people-fill"></i>
                      </div>
                      <div className="company-stat-number">100%</div>
                      <div className="company-stat-label">On-Time Delivery</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="company-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="company-stat-icon">
                        <i className="bi bi-award"></i>
                      </div>
                      <div className="company-stat-number">98%</div>
                      <div className="company-stat-label">Client Satisfaction</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="company-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="company-stat-icon">
                        <i className="bi bi-globe"></i>
                      </div>
                      <div className="company-stat-number">5+</div>
                      <div className="company-stat-label">Industries Served</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="company-hero-buttons">
                <Link to="/contact" style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  minWidth: '220px',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                }}>
                  <i className="bi bi-rocket-takeoff"></i>
                  Start Your Journey
                </Link>
                
                <Link to="/case-studies" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  minWidth: '220px',
                  backdropFilter: 'blur(15px)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}>
                  <i className="bi bi-eye"></i>
                  See Case Studies
                </Link>
              </div>
              
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          .company-hero-badge {
            animation: pulse 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '6rem 0'
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <div className="philosophy-image-container" style={{
                position: 'relative',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
              }}>
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="AI Innovation and Technology" 
                  style={{
                    width: '100%',
                    height: '500px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  padding: '2rem',
                  color: 'white'
                }}>
                  <h4 style={{ margin: 0, fontWeight: '600' }}>Innovation Through Technology</h4>
                  <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>Building the future of AI</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '1.5rem'
              }}>Adding Value Through Intelligent Solutions</h2>
              <p style={{
                fontSize: '1.1rem',
                color: '#64748b',
                lineHeight: '1.7',
                marginBottom: '2rem'
              }}>
                At <span style={{ color: '#0ea5e9', fontWeight: '600' }}>Vallen AI</span>, we believe in adding measurable value to every business we work with. Our approach goes beyond just implementing technology — we focus on delivering intelligent solutions that drive real results and sustainable growth. We build generative AI, machine learning, and custom solutions that transform how businesses operate, making them more efficient, competitive, and future-ready.
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="bi bi-check-circle-fill" style={{ color: '#0ea5e9', fontSize: '1.2rem' }}></i>
                  <span style={{ color: '#374151', fontWeight: '500' }}>Agile & Fast</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="bi bi-check-circle-fill" style={{ color: '#0ea5e9', fontSize: '1.2rem' }}></i>
                  <span style={{ color: '#374151', fontWeight: '500' }}>Personal Touch</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="bi bi-check-circle-fill" style={{ color: '#0ea5e9', fontSize: '1.2rem' }}></i>
                  <span style={{ color: '#374151', fontWeight: '500' }}>Innovation-First</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '6rem 0'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              
              <h2 className="mb-4" style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '800',
                color: '#ffffff',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.2'
              }}>Empowering Businesses with Cutting-Edge AI</h2>
              
              <p className="mb-5" style={{
                fontSize: '1.2rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                To empower businesses with cutting-edge technology solutions, unlocking their growth potential by connecting them with innovative AI capabilities that drive real business transformation and measurable results.
              </p>
              
              <div className="row g-4 mt-5 justify-content-center">
                <div className="col-md-6 col-lg-4">
                  <div className="value-card" style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div className="value-icon" style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      fontSize: '1.5rem',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <i className="bi bi-lightbulb"></i>
                    </div>
                    <h4 style={{ color: 'white', marginBottom: '1rem' }}>Innovation</h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0 }}>
                      Pushing the boundaries of what is possible with AI technology
                    </p>
                  </div>
                </div>
                <div className="col-md-6 col-lg-4">
                  <div className="value-card" style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div className="value-icon" style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      fontSize: '1.5rem',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <i className="bi bi-people"></i>
                    </div>
                    <h4 style={{ color: 'white', marginBottom: '1rem' }}>Accessibility</h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0 }}>
                      Making AI solutions available to businesses of all sizes
                    </p>
                  </div>
                </div>
                <div className="col-md-6 col-lg-4">
                  <div className="value-card" style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div className="value-icon" style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      fontSize: '1.5rem',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <i className="bi bi-graph-up"></i>
                    </div>
                    <h4 style={{ color: 'white', marginBottom: '1rem' }}>Results</h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0 }}>
                      Delivering measurable business impact through AI solutions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '6rem 0'
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '1.5rem'
              }}>Transforming IT Systems into Smart, AI-Driven Assets</h2>
              <p style={{
                fontSize: '1.1rem',
                color: '#64748b',
                lineHeight: '1.7',
                marginBottom: '2rem'
              }}>
                At Valora Next, we envision transforming IT systems into smart, agile, and AI-driven digital assets. With our expertise in emerging technologies, we empower global clients through innovative, adaptive solutions, shaping a future where technology meets the dynamic demands of a connected world.
              </p>
              <div style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                borderRadius: '20px',
                padding: '2rem',
                color: 'white',
                marginBottom: '2rem'
              }}>
                <h4 style={{ marginBottom: '1rem' }}>Our Vision for the Future:</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>Democratize AI access for businesses of all sizes</li>
                  <li style={{ marginBottom: '0.5rem' }}>Create intelligent, adaptive digital ecosystems</li>
                  <li style={{ marginBottom: '0.5rem' }}>Drive sustainable business transformation</li>
                  <li>Build the foundation for next-generation AI solutions</li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6">
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ color: '#1e293b', marginBottom: '1.5rem' }}>Our Vision Pillars</h4>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem'
                    }}>
                      <i className="bi bi-globe"></i>
                    </div>
                    <div>
                      <h5 style={{ margin: 0, color: '#1e293b' }}>Global Impact</h5>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Reaching businesses worldwide</p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem'
                    }}>
                      <i className="bi bi-cpu"></i>
                    </div>
                    <div>
                      <h5 style={{ margin: 0, color: '#1e293b' }}>AI Democratization</h5>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Making AI accessible to all</p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem'
                    }}>
                      <i className="bi bi-heart"></i>
                    </div>
                    <div>
                      <h5 style={{ margin: 0, color: '#1e293b' }}>Human-Centric</h5>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>AI that serves humanity</p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem'
                    }}>
                      <i className="bi bi-rocket"></i>
                    </div>
                    <div>
                      <h5 style={{ margin: 0, color: '#1e293b' }}>Continuous Innovation</h5>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Always pushing boundaries</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section id="challenges" className="py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '6rem 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 15% 85%, rgba(14, 165, 233, 0.12) 0%, transparent 60%),
            radial-gradient(circle at 85% 15%, rgba(14, 165, 233, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 60%),
            radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Subtle Grid Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              
              <h2 className="mb-4" style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#ffffff',
                textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                lineHeight: '1.1',
                marginBottom: '2rem'
              }}>Our Approach to Digital Transformation</h2>
              
              <p className="mb-5" style={{
                fontSize: '1.3rem',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.7',
                maxWidth: '900px',
                margin: '0 auto 3rem',
                fontWeight: '500'
              }}>
                We understand the unique challenges businesses face in today is digital landscape. Our comprehensive approach combines cutting-edge AI technology with proven methodologies to deliver solutions that drive real business transformation.
              </p>
            </div>
          </div>
          
          <div className="row g-4 mt-4">
            <div className="col-lg-6">
              <div className="approach-card" style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                height: '100%',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="approach-icon" style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '2rem',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-lightning"></i>
                </div>
                <h4 style={{ 
                  color: 'white', 
                  marginBottom: '1.5rem',
                  fontSize: '1.4rem',
                  fontWeight: '700'
                }}>Rapid Innovation</h4>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  margin: 0, 
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  We move swiftly, refining our approach with every step to maintain a leading edge. Our agile methodology ensures rapid delivery while maintaining the highest quality standards.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="approach-card" style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                height: '100%',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="approach-icon" style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '2rem',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-people"></i>
                </div>
                <h4 style={{ 
                  color: 'white', 
                  marginBottom: '1.5rem',
                  fontSize: '1.4rem',
                  fontWeight: '700'
                }}>Trusted Partnership</h4>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  margin: 0, 
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  Dependable and steadfast, we are always there when it matters most. Our long-term partnerships are built on trust, transparency, and consistent delivery of exceptional results.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="approach-card" style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                height: '100%',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="approach-icon" style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '2rem',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-award"></i>
                </div>
                <h4 style={{ 
                  color: 'white', 
                  marginBottom: '1.5rem',
                  fontSize: '1.4rem',
                  fontWeight: '700'
                }}>Exceeding Expectations</h4>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  margin: 0, 
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  Exceeding expectations is our standard, going beyond what is assured. We do not just meet requirements, we deliver solutions that surprise and delight, creating lasting value for our clients.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="approach-card" style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                height: '100%',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="approach-icon" style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '2rem',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-chat-dots"></i>
                </div>
                <h4 style={{ 
                  color: 'white', 
                  marginBottom: '1.5rem',
                  fontSize: '1.4rem',
                  fontWeight: '700'
                }}>Clear Communication</h4>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  margin: 0, 
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  Transparent, honest communication keeps everyone on the same page. We believe clear is kind, ensuring all stakeholders understand our approach, progress, and deliverables throughout the project.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Team Section */}
      <section id="team" className="py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '6rem 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              
              <h2 className="mb-4" style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#1e293b',
                lineHeight: '1.1',
                marginBottom: '2rem'
              }}>Meet Our Core Team</h2>
              
              <p className="mb-5" style={{
                fontSize: '1.3rem',
                color: '#64748b',
                lineHeight: '1.7',
                maxWidth: '900px',
                margin: '0 auto 3rem',
                fontWeight: '500'
              }}>
                As a startup, we operate with a lean, focused team of passionate AI experts and technology innovators. Our founders and core team bring together decades of combined experience in AI, machine learning, and software development.
              </p>
            </div>
          </div>
          
          <div className="row g-4 mt-4">
            <div className="col-lg-6">
              <div className="team-card" style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                textAlign: 'center',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="team-avatar" style={{
                  width: '140px',
                  height: '140px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '3.5rem',
                  color: 'white',
                  boxShadow: '0 12px 30px rgba(14, 165, 233, 0.3)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-person"></i>
                </div>
                <h4 style={{ 
                  color: '#1e293b', 
                  marginBottom: '1rem',
                  fontSize: '1.5rem',
                  fontWeight: '700'
                }}>AI & ML Experts</h4>
                <p style={{ 
                  color: '#64748b', 
                  marginBottom: '0',
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  Our core team includes specialists in Generative AI, Machine Learning, and Deep Learning with expertise in cutting-edge frameworks and technologies.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="team-card" style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                textAlign: 'center',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div className="team-avatar" style={{
                  width: '140px',
                  height: '140px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '3.5rem',
                  color: 'white',
                  boxShadow: '0 12px 30px rgba(14, 165, 233, 0.3)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}>
                  <i className="bi bi-code-slash"></i>
                </div>
                <h4 style={{ 
                  color: '#1e293b', 
                  marginBottom: '1rem',
                  fontSize: '1.5rem',
                  fontWeight: '700'
                }}>Software Engineers</h4>
                <p style={{ 
                  color: '#64748b', 
                  marginBottom: '0',
                  lineHeight: '1.7',
                  fontSize: '1rem'
                }}>
                  Full-stack developers and DevOps engineers with expertise in modern technologies, cloud platforms, and scalable architecture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '6rem 0'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              
              <h2 className="mb-4" style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                color: '#1e293b',
                lineHeight: '1.2'
              }}>Ready to Transform Your Business?</h2>
              
              <p className="mb-5" style={{
                fontSize: '1.2rem',
                color: '#64748b',
                lineHeight: '1.6',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                Let us discuss how our AI solutions can drive your business forward. We are here to help you navigate the future of technology.
              </p>
            </div>
          </div>
          
          <div className="row g-4 mt-4">
            <div className="col-sm-6 col-lg-3">
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  color: 'white'
                }}>
                  <i className="bi bi-envelope"></i>
                </div>
                <div>
                  <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Email Us</h4>
                  <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                    Send us a message and we will get back to you within 24 hours.
                  </p>
                </div>
                <a href="mailto:contact@vallenai.com" style={{
                  color: '#0ea5e9',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}>
                  contact@vallenai.com
                </a>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  color: 'white'
                }}>
                  <i className="bi bi-calendar-check"></i>
                </div>
                <div>
                  <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Schedule a Call</h4>
                  <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                    Book a free consultation to discuss your AI needs and requirements.
                  </p>
                </div>
                <a href="/contact" style={{
                  color: '#0ea5e9',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}>
                  Book Consultation
                </a>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  color: 'white'
                }}>
                  <i className="bi bi-chat-dots"></i>
                </div>
                <div>
                  <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Live Chat</h4>
                  <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                    Chat with our team in real-time for immediate assistance.
                  </p>
                </div>
                <a href="/contact" style={{
                  color: '#0ea5e9',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}>
                  Start Chat
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <OurCommitment />

      {/* CTA Section */}
      <CTA {...generateCTAMessage('page', { type: 'company' })} />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '50px',
            height: '50px',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
            border: 'none',
            borderRadius: '50%',
            color: 'white',
            fontSize: '1.2rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.2)';
          }}
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}
    </>
  );
};

export default CompanyPage; 